// #25 automatic payments, phase 6 (round 15): the pure logic behind signing a change of the automatic payment card. The
// card change is a scheduled record on the company's public ledger account (CARD_CHANGE: 1 tinybar from the company account
// to its operating account), whose memo says who changed the card, with no card details. The account signatories sign it
// on their collect-signatures card through the same server-built ScheduleSign as a change of amounts: the operating account
// created the schedule and pays it when it runs; nodeStream's platform account pays every signature.
import type { PublicLedgerCardChange } from '../models/public-ledger.js';
import type { ApproveScheduleAccounts } from './public-ledger-sign-check.js';

// What the signing card says above the memo, naming who changed the card (their name as the company's people show it)
export function cardChangeSignCopy(name: string | null | undefined): string {
  const who = name?.trim() || 'A signatory';
  return `${who} changed the company's automatic payment card. Your signature approves the change on the company's public ledger account, where it is recorded as below, with no card details. Once the signatories have signed, the new card becomes the company's automatic payment card and the old one is removed. nodeStream pays the fee for your signature; the company's operating account paid to set the change up.`;
}

// The server's own refusal when the card change no longer waits (public-ledger/autopay/prepare-signature)
export const CARD_CHANGE_GONE_COPY = 'This change of card is no longer waiting for signatures.';
// The memo served differs from the one the card showed: the change was updated as the signatory pressed Sign
export const CARD_CHANGE_CHANGED_COPY =
  'This change was updated just as you signed, so nothing was signed. Please check it again and sign.';

/**
 * Why the card must not sign what the server prepared, or null when it may: the memo the server signs for must be the one
 * the card showed (the record the row names). No memo served means the change no longer waits.
 */
export function cardChangeSignRefusal(
  servedMemo: string | null | undefined,
  shownMemo: string | null | undefined,
): string | null {
  if (servedMemo && shownMemo && servedMemo === shownMemo) return null;
  if (!servedMemo) return CARD_CHANGE_GONE_COPY;
  return CARD_CHANGE_CHANGED_COPY;
}

const normalisedId = (id: unknown) => {
  const match = typeof id === 'string' ? /^(\d+)\.(\d+)\.(\d+)$/.exec(id) : null;
  return match ? `${Number(match[1])}.${Number(match[2])}.${Number(match[3])}` : null;
};

/**
 * Who the card change's row says made and pays its schedule, and who pays each signature (the twin of
 * approveScheduleAccounts for a change of amounts), or null when the row doesn't say it plainly (nothing is then signed): the
 * schedule is made and paid by one account, the company's operating account, and every signature paid by another,
 * nodeStream's platform account. The caller checks the operating account is the company's own.
 */
export function cardChangeScheduleAccounts(
  cardChange: PublicLedgerCardChange | null | undefined,
): ApproveScheduleAccounts | null {
  const creator = normalisedId(cardChange?.creatorAccountId);
  const payer = normalisedId(cardChange?.payerAccountId);
  const signPayer = normalisedId(cardChange?.signPayerAccountId);
  const operating = normalisedId(cardChange?.operatingAccountId);
  if ((cardChange?.payer ?? 'OPERATING') !== 'OPERATING') return null;
  if (!creator || !payer || !signPayer || creator !== payer || signPayer === creator) return null;
  if (operating && operating !== creator) return null;
  return {
    operating: true,
    creatorAccountId: cardChange?.creatorAccountId as string,
    payerAccountId: cardChange?.payerAccountId as string,
    signPayerAccountId: cardChange?.signPayerAccountId as string,
  };
}
